import { ConversationsListSchema } from "@/features/Prompt/schema";
import { getAuthorized } from "@/lib/fetch";
import { useThreadRuntime } from "@assistant-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useClient } from "./ClientContext";
import {
  determineIfNewThread,
  extractThreadIdFromUrl,
  handleThreadCompletion,
  hasThreadJustCompleted,
} from "./utils";

/**
 * Hook for fetching the list of conversations from the API
 * @returns Query result containing conversations data
 */
export function useConversations() {
  const client = useClient();

  const { data } = useQuery({
    queryKey: ["threads", client],
    queryFn: async () => {
      const response = await getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/conversations?limit=100`,
      ).then((res) => res.json());

      const parsedData = ConversationsListSchema.safeParse(response);

      if (!parsedData.success) {
        console.error("Validation error:", parsedData.error);
        return [];
      }

      return parsedData.data;
    },
  });

  return {
    data,
  };
}

/**
 * Hook to handle conversation refresh when a new thread is completed
 *
 * This hook monitors thread state changes and refreshes the conversation list
 * when a new thread is created and completed.
 *
 * Important: We use `window.location.pathname` directly to get the current URL path
 * because RuntimeProvider.tsx directly manipulates the URL using window.history.pushState
 * when receiving a new conversation ID from the server. This bypasses React Router's state,
 * so we need to access the DOM location directly to get accurate information.
 * Then we use the threadId to navigate to the conversation URL and refresh the react router state.
 *
 */
export function useRefeshConversationOnNewThread() {
  const runtime = useThreadRuntime();
  const previousIsRunning = useRef(false);
  const isNew = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = runtime.subscribe(() => {
      const state = runtime.getState();
      const isRunning = state.isRunning;

      const threadId = extractThreadIdFromUrl();

      isNew.current = determineIfNewThread(isNew.current, threadId, isRunning);

      if (
        hasThreadJustCompleted(
          isNew.current,
          isRunning,
          previousIsRunning.current,
        )
      ) {
        handleThreadCompletion(threadId, queryClient, navigate);
        previousIsRunning.current = false;
        isNew.current = false;
      } else {
        previousIsRunning.current = isRunning;
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient, navigate]);
}
